import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, XCircle } from 'lucide-react';
import { validateInviteToken } from '../../services/api';
import { AuthSplitShell } from '../../components/auth/AuthSplitShell';
import { getReleaseDateLabel } from '../../config/releaseDate';
import { authPrimaryButton } from '../../components/auth/authViewTheme';

const INVALID_MESSAGE = 'This invite link is invalid or has expired';

/**
 * Validates an organisation invite token, then sends the user to the login page
 * to sign in with Google (account creation happens on the server during OAuth).
 * URL: /invite/accept?token=...
 */
export default function InviteAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const releaseDateLabel = getReleaseDateLabel();
  const [status, setStatus] = useState('loading'); // loading | invalid
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setMessage(INVALID_MESSAGE);
      return;
    }

    let cancelled = false;
    validateInviteToken(token)
      .then((data) => {
        if (cancelled) return;
        if (data?.valid) {
          navigate('/', { replace: true });
          return;
        }
        setStatus('invalid');
        setMessage(data?.message || INVALID_MESSAGE);
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('invalid');
        setMessage(INVALID_MESSAGE);
      });

    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <AuthSplitShell
      title={status === 'invalid' ? 'Invite unavailable' : 'Checking invite'}
      description={status === 'invalid' ? message : 'Please wait…'}
      releaseDateLabel={releaseDateLabel}
    >
      {status === 'loading' ? (
        <div className="flex flex-col items-center py-6">
          <Loader2 className="mb-4 h-12 w-12 animate-spin text-brand-purple" aria-hidden />
          <p className="text-sm text-slate-400">Validating your invite…</p>
        </div>
      ) : (
        <div className="flex flex-col items-center py-4">
          <XCircle className="mb-4 h-12 w-12 text-red-400" aria-hidden />
          <p className="text-center text-sm text-slate-300">{message}</p>
          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className={`${authPrimaryButton} mt-6`}
          >
            Go to sign in
          </button>
        </div>
      )}
    </AuthSplitShell>
  );
}
